import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle2, ChevronRight, Play, BookOpen } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useSmartBack } from '../hooks/useSmartBack';
import LevelMap, { PHASES } from './LevelMap';
import LevelView from './LevelView';
import { LEVELS_DATA_FINAL } from './LEVELS_DATA_FINAL';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getPhase = (id) => PHASES.find(p => id >= p.range[0] && id <= p.range[1]) ?? PHASES[0];

export default function LearningSection_v2({ onBack }) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const handleBack = onBack ?? smartBack;
  const [searchParams, setSearchParams] = useSearchParams();

  const view               = searchParams.get('view') || 'map';
  const selectedLevel      = searchParams.get('level') ? Number(searchParams.get('level')) : null;
  const selectedCardIndex  = searchParams.get('card') ? Number(searchParams.get('card')) : 0;

  const setView = (v) =>
    setSearchParams(p => { const n = new URLSearchParams(p); n.set('view', v); return n; }, { replace: true });

  const [levels, setLevels]           = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [isLoading, setIsLoading]       = useState(true);

  useEffect(() => { fetchLearningProgress(); }, []);

  const fetchLearningProgress = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}/learning/progress`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLevels(data.levels || []);
        setUserProgress(data || {});
      }
    } catch (e) {
      console.error('Failed to fetch learning progress:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardViewed = (levelId, cardIndex) => {
    fetch(`${API_URL}/learning/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ level_id: levelId, card_index: cardIndex }),
    }).catch(e => console.error('Failed to mark card as viewed:', e));
  };

  const handleCheckpointAnswered = (levelId, checkpointIndex, selectedOption) => {
    fetch(`${API_URL}/learning/checkpoint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      body: JSON.stringify({ level_id: levelId, checkpoint_index: checkpointIndex, selected_option: selectedOption }),
    }).catch(e => console.error('Failed to submit checkpoint:', e));
  };

  const handleLevelComplete = async (levelId) => {
    await fetchLearningProgress();
    const nextId = Number(levelId) + 1;
    if (LEVELS_DATA_FINAL.some(l => l.id === nextId)) {
      setSearchParams(p => { const n = new URLSearchParams(p); n.set('level', nextId); n.set('card', 0); n.set('view', 'card'); return n; }, { replace: true });
      return;
    }
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('level'); n.set('view', 'map'); return n; }, { replace: true });
  };

  // Tile click → level overview first, then user picks a card
  const handleSelectLevel = (levelId) => {
    setSearchParams(p => {
      const n = new URLSearchParams(p);
      n.set('level', levelId);
      n.set('view', 'level');
      return n;
    }, { replace: true });
  };

  const handleBackToMap = () => {
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('level'); n.delete('card'); n.set('view', 'map'); return n; }, { replace: true });
    fetchLearningProgress();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-1" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-t-4 rounded-full animate-spin mx-auto mb-3"
            style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
          <p className="text-[14px]" style={{ color: colors.primaryText }}>Loading your learning journey...</p>
        </div>
      </div>
    );
  }

  const selectedLevelData = selectedLevel
    ? LEVELS_DATA_FINAL.find(l => l.id === selectedLevel)
    : null;

  const effectiveView = ((view === 'level' || view === 'card') && !selectedLevelData) ? 'map' : view;

  return (
    <div style={{ backgroundColor: colors.background }}>

      {/* ── Map header ── */}
      {effectiveView === 'map' && (
        <div style={{ padding: '20px 16px 4px' }}>
          <p style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6366F1', marginBottom: 3 }}>Learning</p>
          <p style={{ fontSize: 20, fontWeight: 800, color: '#eceef5', letterSpacing: '-0.02em', lineHeight: 1.1 }}>Courses & Levels</p>
        </div>
      )}

      <div className="pt-1">
        {effectiveView === 'map' && (
          <div data-tour="learning-all-levels">
            <LevelMap
              levels={levels}
              currentLevel={selectedLevel}
              onSelectLevel={handleSelectLevel}
              onNavigate={navigate}
            />
          </div>
        )}

        {/* Level overview — shown when coming back from card reader */}
        {effectiveView === 'level' && selectedLevelData && (
          <LevelOverview
            level={selectedLevelData}
            userProgress={userProgress}
            onBack={handleBackToMap}
            onSelectCard={(cardIndex) => {
              setSearchParams(p => { const n = new URLSearchParams(p); n.set('card', cardIndex); n.set('view', 'card'); return n; }, { replace: true });
            }}
          />
        )}

        {effectiveView === 'card' && selectedLevelData && (
          <LevelView
            key={`l${selectedLevelData.id}`}
            level={selectedLevelData}
            userProgress={userProgress}
            riskProfileFilled={!!userProgress.risk_profile_completed}
            initialCardIndex={selectedCardIndex}
            onBack={() => setView('level')}
            onCardViewed={handleCardViewed}
            onCheckpointAnswered={handleCheckpointAnswered}
            onLevelComplete={handleLevelComplete}
            onNavigateToRiskProfile={() => navigate('/profile?section=risk-profile')}
          />
        )}
      </div>
    </div>
  );
}

// ─── Level Overview — hero + card list, replaces Level1CardView ───────────────
function LevelOverview({ level, userProgress, onBack, onSelectCard }) {
  const { colors } = useTheme();
  const phase        = getPhase(level.id);
  const cards        = level?.cards || [];
  const totalCards   = cards.length;
  const completedCount = userProgress?.levels?.[level.id]?.cards_viewed || 0;
  const pct          = totalCards > 0 ? Math.round((completedCount / totalCards) * 100) : 0;
  const isComplete   = completedCount >= totalCards;
  const resumeCard   = isComplete ? 0 : completedCount;

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <div style={{
        padding: '0 16px 20px',
        background: `linear-gradient(160deg, ${phase.color}18 0%, ${phase.color}06 60%, transparent 100%)`,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        {/* Nav row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0 20px' }}>
          <button
            onClick={onBack}
            style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <ArrowLeft size={16} color="#eceef5" />
          </button>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: phase.color }}>
            {phase.label}
          </span>
        </div>

        {/* Level badge + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            height: 26, paddingInline: 10, borderRadius: 8, flexShrink: 0,
            background: `${phase.color}22`, border: `1px solid ${phase.color}45`,
            display: 'flex', alignItems: 'center',
          }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: phase.color, letterSpacing: '0.04em' }}>
              Level {level.id}
            </span>
          </div>
          {isComplete && (
            <span style={{ fontSize: 10, fontWeight: 700, color: phase.color }}>✓ Completed</span>
          )}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#eceef5', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 8 }}>
          {level.name}
        </h1>

        {level.description && (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: 16 }}>
            {level.description}
          </p>
        )}

        {/* Progress bar */}
        <div data-tour="learning-progress-banner" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{completedCount} of {totalCards} lessons</span>
            <span style={{ fontSize: 11, color: phase.color, fontWeight: 700 }}>{pct}%</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(pct > 0 ? 4 : 0, pct)}%`, background: phase.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
          </div>
        </div>

        {/* Start / Continue CTA */}
        <button
          onClick={() => onSelectCard(resumeCard)}
          onMouseEnter={e => e.currentTarget.style.borderColor = `${phase.color}80`}
          onMouseLeave={e => e.currentTarget.style.borderColor = `${phase.color}45`}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '13px', borderRadius: 14, cursor: 'pointer',
            background: `linear-gradient(135deg, ${phase.color}22 0%, ${phase.color}0e 100%)`,
            border: `1px solid ${phase.color}45`,
            transition: 'border-color 0.18s ease',
          }}
        >
          <Play size={14} color={phase.color} fill={phase.color} />
          <span style={{ fontSize: 14, fontWeight: 800, color: phase.color }}>
            {completedCount === 0 ? 'Start Level' : isComplete ? 'Review from Start' : `Continue · Lesson ${completedCount + 1}`}
          </span>
        </button>
      </div>

      {/* ── Card list ── */}
      <div data-tour="learning-modules-list" style={{ padding: '0 16px 32px' }}>

        {/* Section heading — Discover style */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 14px' }}>
          <BookOpen size={12} color={phase.color} />
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: phase.color }}>
            All Lessons
          </span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          <span style={{
            fontSize: 10, fontWeight: 700, color: phase.color,
            background: `${phase.color}15`, border: `1px solid ${phase.color}30`,
            padding: '2px 8px', borderRadius: 8,
          }}>
            {completedCount}/{totalCards}
          </span>
        </div>

        {cards.map((card, i) => {
          const done    = i < completedCount;
          const current = i === completedCount && !isComplete;
          const borderC = done ? `${phase.color}45` : current ? `${phase.color}50` : `${phase.color}18`;

          return (
            <button
              key={card.id || i}
              onClick={() => onSelectCard(i)}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${phase.color}65`}
              onMouseLeave={e => e.currentTarget.style.borderColor = borderC}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 12px', borderRadius: 14, marginBottom: 8,
                cursor: 'pointer', textAlign: 'left',
                background: done
                  ? `linear-gradient(135deg, ${phase.color}18 0%, ${phase.color}06 100%)`
                  : current
                  ? `linear-gradient(135deg, ${phase.color}14 0%, ${phase.color}04 100%)`
                  : `linear-gradient(135deg, ${phase.color}0a 0%, transparent 100%)`,
                border: `1px solid ${borderC}`,
                transition: 'border-color 0.18s ease',
              }}
            >
              {/* Number / check badge */}
              <div style={{
                width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                background: done ? phase.color : current ? `${phase.color}25` : `${phase.color}14`,
                border: done ? 'none' : `1px solid ${phase.color}35`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {done
                  ? <CheckCircle2 size={15} color="#fff" />
                  : <span style={{ fontSize: 11, fontWeight: 800, color: current ? phase.color : `${phase.color}90` }}>{i + 1}</span>
                }
              </div>

              {/* Card info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: 13, fontWeight: done ? 600 : current ? 700 : 500,
                  color: done || current ? '#eceef5' : 'rgba(255,255,255,0.5)',
                  lineHeight: 1.3, marginBottom: 2,
                }}>
                  {card.title}
                </p>
                {card.rule && (
                  <p style={{ fontSize: 10, color: current ? phase.color : 'rgba(255,255,255,0.28)', fontWeight: 600, letterSpacing: '0.02em' }}>
                    {card.rule.length > 55 ? card.rule.substring(0, 55) + '…' : card.rule}
                  </p>
                )}
              </div>

              <ChevronRight size={14} color={`${phase.color}50`} style={{ flexShrink: 0 }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
